<template>
  <div class="overview-page">
    <div class="line-separator" />
    <RexSpinV3 :spinning="isLoading">
      <div class="overview-header">
        <AWSHeader
          @handle-subscription-select="handleSubscriptionSelect"
          :subscriptions="subscriptions"
          :isShowSelectDb="false"
        />
      </div>

      <h4 class="overview-text">
        {{ t('aws.overviewHeading') }}
      </h4>

      <div class="overview-tiles">
        <AWSTile :tiles="tilesData" />

        <AWSAlertTable
          :toggleOptions="alertTableToggleOptions()"
          :alertsData="alertTableItems"
          :totalAlertsData="totalAlertsData"
          alertType="subscription"
        />
      </div>
      <div class="overview-subscription-table">
        <AWSTable
          :placeholder="t('aws.searchSubscriptionId')"
          :title="t('aws.subscriptionOverview')"
          :headers="lambdaOverviewHeader"
          :data="filteredSubscriptionMatrices"
          @subscriptionClicked="handleSubscriptionClick"
          isToggleEnabled="true"
          :currentSubType="getCurrentSubType"
        />
      </div>
    </RexSpinV3>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';

import AWSTile from '@/components/Cloud/Dashboard/Components/aws-tiles.vue';
import AWSHeader from '@/components/Cloud/Dashboard/Components/aws-overview-header.vue';
import AWSTable from '@/components/Cloud/Dashboard/Components/aws-table.vue';
import AWSAlertTable from '@/components/Cloud/Dashboard/Components/aws-alert-table.vue';
import RexSpinV3 from '@/rex-components/RexSpinV3.vue';
import {
  alertTableToggleOptions,
  alertsMapper,
  lambdaOverviewHeader as lambdaOverviewHeaderType,
  lambdaOverviewTableHeader,
  subTypes,
} from '@/components/Cloud/Dashboard/cloud.types';
import { useStoreData } from '@/components/Common/useStoreData';
import { useStoreDataCloud } from '@/components/Cloud/Dashboard/useStoreData';
import { notificationError } from '@/utils/notification';
import { permissionTypes } from '@/router/constant';
// Setup i18n
const { t } = useI18n();

// Store references
const route = useRoute();
const router = useRouter();
const { getDurationTime } = useStoreData();
const {
  fetchAwsServices,
  fetchReadMatricListDataForServices,
  getTopAlertsPerInstances,
  getTopAlertsPerService,
  setCurrentPage,
  setCurrentService,
  setCurrentServices,
  getCurrentServices,
} = useStoreDataCloud();

// Reactive state
const lambdaOverviewHeader = computed(() => [
  ...lambdaOverviewTableHeader(),
  ...lambdaOverviewHeaderType(),
]);
const subscriptions = ref([]);
const subscriptionMatrices = ref([]);
const filteredSubscriptionMatrices = ref([]);
const tilesData = ref([]);
const alertTableItems = ref([]);
const totalAlertsData = ref([]);
const isLoading = ref(false);

// Computed properties
const getAlertMatricNameMapper = computed(() => alertsMapper);
const getCurrentSubType = computed(() => subTypes.LAMBDA);

// Handlers
const handleSubscriptionSelect = (selectedSubscriptions) => {
  filteredSubscriptionMatrices.value =
    selectedSubscriptions.length > 0
      ? subscriptionMatrices.value
          .filter((matrices) =>
            selectedSubscriptions.includes(matrices.subscriptionId),
          )
          .slice(0, 5)
      : [];

  setCurrentServices(
    subscriptions.value
      .filter((item) => selectedSubscriptions.includes(item.label))
      .map((item) => item.key)
      .slice(0, 5),
  );
};

const handleSubscriptionClick = (subscriptionId) => {
  const service = route.params.service;
  const serviceId = subscriptions.value.find(
    (service) => service.label === subscriptionId,
  )?.key;
  setCurrentService(serviceId);
  router.push({
    name: 'subscriptionId',
    params: { subscriptionId, serviceId, service },
  });
};

const getInitialData = async (params) => {
  const { isInitialRender } = params ?? {};
  try {
    isLoading.value = true;
    const services = await fetchAwsServices({
      duration: getDurationTime.value,
      serviceReq: { capability: 'C', subType: 'LAMBDA' },
      permissions: [permissionTypes.READ, permissionTypes.WRITE],
    });

    subscriptions.value = services.map((service) => ({
      ...service,
      selected: false,
    }));

    setCurrentServices(
      !isInitialRender
        ? subscriptions.value
            .filter((subscription) =>
              getCurrentServices.value.includes(subscription.key),
            )
            .map((item) => item.key)
        : getCurrentServices.value.length
          ? subscriptions.value
              .filter((subscription) =>
                getCurrentServices.value.includes(subscription.key),
              )
              .map((item) => item.key)
          : subscriptions.value.map((item) => item.key).slice(0, 1),
    );

    const promises = [
      getMatricesData(getDurationTime.value),
      getServiceLevelTopAlert(getDurationTime.value),
    ];

    await Promise.allSettled(promises);
    await getTopAlertsByMetrices(subscriptions.value);
  } catch (error) {
    console.error('Error in beforeMount:', error);
  } finally {
    isLoading.value = false;
  }
};

const getMatricesData = async (currentDurationTime) => {
  try {
    const matrices = await fetchReadMatricListDataForServices({
      headers: lambdaOverviewHeader.value,
      iterateInstance: subscriptions.value,
      getDurationTime: currentDurationTime,
    });
    subscriptionMatrices.value = matrices;
    filteredSubscriptionMatrices.value = matrices.filter((matric) =>
      getCurrentServices.value.includes(matric.serviceId),
    );
  } catch (err) {
    notificationError(err?.message ?? t('aws.getMatricDataFailed'));
  }
};

const getServiceLevelTopAlert = async (currentDurationTime) => {
  const topAlertPerService = await getTopAlertsPerService({
    duration: currentDurationTime,
    condition: { entity: { scope: 'All' }, capability: 'C', subType: 'LAMBDA' },
  });

  alertTableItems.value =
    topAlertPerService.data.getTopAlertsPerService.alertsApps.map((data) => ({
      subscriptionId: data.serviceName,
      alerts: data.count,
    }));
};

const handleFilterSubscriptionMatrices = async (currentValue) => {
  try {
    tilesData.value = [
      ...lambdaOverviewHeaderType().map((header) => {
        const key = header.key.toLowerCase();
        const isAverage = header.value === 'avg';
        let content = currentValue.reduce((accumulator, currValue) => {
          accumulator += parseInt(currValue[key]);
          return accumulator;
        }, 0);
        const average = content / currentValue.length;
        content = isAverage ? (isNaN(average) ? 0 : average) : content;
        return {
          title: header.tileName,
          content:
            typeof content === 'object'
              ? content[key]?.toFixed?.(isAverage ? 2 : 0)
              : content?.toFixed?.(isAverage ? 2 : 0),
          tooltip: header.tooltip,
        };
      }),
    ];
  } catch (error) {
    console.error('Error in filteredSubscriptionMatrices watcher:', error);
  }
};

const getTopAlertsByMetrices = async (subscriptions) => {
  const alerts = [];
  for (let subscription of subscriptions) {
    const topAlertsByMetircs = await getTopAlertsPerInstances({
      condition: {
        entity: { scope: 'Service', serviceName: subscription.key },
      },
      duration: getDurationTime.value,
    });
    const matrices = topAlertsByMetircs?.data?.getTopMetricsByAlerts?.values;

    const subscriptionMetrics = {};

    for (let matric of matrices) {
      subscriptionMetrics[matric.metricName] = matric.alertCount;
    }

    alerts.push(subscriptionMetrics);
  }
  let totalAlerts = {};
  for (let individualMetrics in getAlertMatricNameMapper.value) {
    totalAlerts[individualMetrics] = alerts.reduce(
      (currentAlert, accumulator) =>
        accumulator[individualMetrics]
          ? currentAlert + accumulator[individualMetrics]
          : currentAlert,
      0,
    );
  }

  totalAlertsData.value = Object.entries(totalAlerts)
    .map(([key, value]) => ({
      alerts: value,
      metrics: t(`aws.alertsTable.${getAlertMatricNameMapper.value[key]}`),
    }))
    .filter((alert) => alert.alerts)
    .sort((a, b) => b.alerts - a.alerts);
};

// Watchers
watch(filteredSubscriptionMatrices, handleFilterSubscriptionMatrices);
watch(getDurationTime, async (currentDurationTime) => {
  await getInitialData({
    isInitialRender: true,
  });
  await getMatricesData(currentDurationTime);
  await getServiceLevelTopAlert(currentDurationTime);
});

// Lifecycle hooks
onMounted(() => {
  setCurrentPage('overview');
  getInitialData({
    isInitialRender: true,
  });
});
</script>

<style lang="scss">
.overview-text {
  display: flex;
  margin-bottom: 1.25rem;
  color: $color-brand-accent;
}
.overview-tiles {
  display: flex;
  flex-direction: row;
  gap: 1.25rem;
  justify-content: space-between;
  margin-top: 1.25rem;
  margin-bottom: 1.25rem;
}

.line-separator {
  border: 0.03125em solid $line-separator;
  margin-top: 0.6875em;
  opacity: 80%;
  width: 100%;
}

.overview-subscription-table {
  margin-top: 1.25rem;
  margin-bottom: 3.125rem;
}
</style>
